import Sheet, { SheetRef } from 'react-modal-sheet';
import BuildSheet from 'components/build-sheet/build-sheet';
import MaterialsSidebar from 'components/materialssidebar/materials-sidebar';
import Notification from 'components/notification/notification';
import InfoBanner from 'components/info-banner/info-banner';
import { isUrbanPavilionProduct } from 'state/appstate/appstate';
import OptionsSidebarSmall from 'components/optionssidebar/options-sidebar-small';
import OptionParametersSidebar from 'components/option-parameters-sidebar/option-parameters-sidebar';
import QuoteSidebar from 'components/quote-sidebar/quote-sidebar';
import { ROUTES } from 'helpers/constants';
import useWindowDimensions from 'helpers/useWindowDimensions';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useNotificationStore } from 'state/notificationstate/notificationstate';
import { useUiState } from 'state/ui-state/ui-state';
import RunningTotal from 'components/runningtotal/running-total';
import './viewer-screen-small.css';
import { useOnClickOutside } from 'helpers/useOnClickOutside';
import { ActionType } from '@swd_admin/cdc-viewer';
import { useViewerState } from 'state/viewer-state/viewer-state';

const ViewerScreenSmall: React.FunctionComponent<{
  bottomSheetHeightChange: (height: number) => void;
}> = ({ children, bottomSheetHeightChange }) => {
  const { height } = useWindowDimensions();
  const HEADER_HEIGHT = 60;
  const CLOSED_HEADER_OFFSET = 60;
  const SHEET_HANDLE_HEIGHT = 40;
  const FOOTER_BUTTONS_HEIGHT = 42;
  const FOOTER_RUNNING_TOTAL_HEIGHT = 42;
  const FOOTER_HEIGHT = FOOTER_BUTTONS_HEIGHT + FOOTER_RUNNING_TOTAL_HEIGHT;
  const initialYClosed = FOOTER_HEIGHT + SHEET_HANDLE_HEIGHT;
  const snapPoints = [ -HEADER_HEIGHT, (height - CLOSED_HEADER_OFFSET) / 2, initialYClosed ];
  const initialSnap = 1;
  const closedSnap = 2;
  const notificationRef = React.useRef<HTMLDivElement>(null);
  const setNotification = useNotificationStore(
    (state) => state.setNotification
  );
  const ref = React.useRef<SheetRef>();
  const sheetContent = React.useRef<SheetRef>();
  const [userDefinedSnap, setSnap] = useState<number>(initialSnap);
  const [bottomSheetHeight, setBottomSheetHeight] = useState<number>(0);
  const [sheetContentHeight, setSheetContentHeight] = useState<number>(0);
  const [footerHeight, setFooterHeight] = useState<number>(0);

  const sheetContents = {
    [ROUTES.MATERIAL_SCREEN]: <MaterialsSidebar />,
    [ROUTES.BUILD_SCREEN]: <BuildSheet />,
    [ROUTES.OPTIONS_SCREEN]: <OptionsSidebarSmall />,
    [ROUTES.QUOTE_SCREEN]: <QuoteSidebar />,
  };
  const { pathname } = useLocation();
  const isQuoteStep = pathname === ROUTES.QUOTE_SCREEN;
  const {
    activeOption,
    setActiveOption,
    mobileBottomSheetSnap,
    setMobileBottomSheetSnap,
    focusType,
    setFocusType
  } = useUiState();
  const { viewer } = useViewerState();

  // Move sheet to middle on change screen
  useEffect(() => {
    ref.current?.snapTo(initialSnap);
    setBottomSheetHeight(snapPoints[initialSnap]);
  }, [pathname]);

  useEffect(() => {
    const value = snapPoints[Math.abs(userDefinedSnap)];
    setBottomSheetHeight(value > 0 ? value : height + value);
  }, [userDefinedSnap]);

  useEffect(() => {
    if (mobileBottomSheetSnap !== userDefinedSnap) {
      ref.current?.snapTo(mobileBottomSheetSnap);
      const value = snapPoints[Math.abs(mobileBottomSheetSnap)];
      setBottomSheetHeight(value > 0 ? value : height + value);
    }
  }, [mobileBottomSheetSnap]);

  useEffect(() => {
    setFooterHeight(activeOption
      ? 0
      : isQuoteStep
        ? FOOTER_BUTTONS_HEIGHT
        : FOOTER_HEIGHT)
  }, [activeOption, isQuoteStep])

  useEffect(() => {
    bottomSheetHeightChange(mobileBottomSheetSnap === 0 ? 0 : bottomSheetHeight);
    setSheetContentHeight(bottomSheetHeight - SHEET_HANDLE_HEIGHT - footerHeight);
  }, [bottomSheetHeight])

  useOnClickOutside(sheetContent, () => {
    if (activeOption) {
      setActiveOption(undefined);
    }
  });

  useEffect(() => {
    if (pathname !== ROUTES.OPTIONS_SCREEN && focusType !== null) {
      viewer?.dispatch({
        type: ActionType.SetFocus,
        focusType: null,
      });
      setFocusType(null);
    }
  }, [pathname])

  return (
    <>
      <div
        ref={notificationRef}
        style={{ position: 'relative', width: '100%', height: '100%' }}
      >
        {isUrbanPavilionProduct() && (
          <InfoBanner message="Please note the concrete slab needs to be larger than the footprint of the Urban Pavilion to accommodate the post base brackets (not shown in our Custom Design Center)." />
        )}
        {children}
      </div>
      <Notification parent={notificationRef} />

      <Sheet
        isOpen={true}
        onSnap={(s) => {
          setSnap(Math.abs(s));
          setMobileBottomSheetSnap(Math.abs(s));
        }}
        ref={ref}
        snapPoints={snapPoints}
        initialSnap={initialSnap}
        onClose={() => ref.current?.snapTo(closedSnap)}
      >
        <Sheet.Container
          ref={sheetContent}
          style={{
            boxShadow: 'none',
            backgroundColor: Theme['@grey-lightest'],
          }}
        >
          <Sheet.Header
            style={{
              pointerEvents: 'auto',
              backgroundColor: Theme['@grey-lightest'],
            }}
          />
          <Sheet.Content
            style={{
              height: `${sheetContentHeight}px`,
              flex: 'initial',
            }}
          >
            {activeOption ? (
              <OptionParametersSidebar />
            ) : (
              sheetContents[pathname]
            )}
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
      {(activeOption && <></>) || (
        <RunningTotal
          style={{
            position: 'fixed',
            bottom: 0,
            zIndex: 10000000,
            width: '100%',
            backgroundColor: Theme['@grey-lightest'],
          }}
        />
      )}
    </>
  );
};

export default ViewerScreenSmall;
